import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    Zap,
    Shield,
    SunDim,
    Cpu,
    Clock,
    ArrowRight,
    X,
    Award,
    Users,
    CheckCircle,
    Globe,
    ZapIcon,
    BarChart2,
    Code,
    ChevronLeft,  // Add these
    ChevronRight,  // Add these
    Menu,
} from 'lucide-react';

const HomePage = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeService, setActiveService] = useState(null);
    const [activeProfile, setActiveProfile] = useState(0);
    const navigate = useNavigate();


    const services = [
        {
            icon: <SunDim className="w-12 h-12 text-yellow-500" />,
            title: "Renewable Energy",
            description: "Advanced solar and inverter power solutions tailored for industrial and residential needs.",
            details: [
                "Solar system design and installation",
                "Inverter power optimization",
                "Energy efficiency consulting"
            ]
        },
        {
            icon: <Cpu className="w-12 h-12 text-blue-600" />,
            title: "Electrical Infrastructure",
            description: "Comprehensive electrical systems design, installation, and maintenance.",
            details: [
                "Industrial and domestic wiring",
                "Data center network infrastructure",
                "Electrical project design and consulting"
            ]
        },
        {
            icon: <Shield className="w-12 h-12 text-green-500" />,
            title: "Advanced Technical Services",
            description: "Specialized technical solutions for complex infrastructure challenges.",
            details: [
                "X-Ray scanner installation",
                "Power protection systems",
                "Advanced cybersecurity services"
            ]
        }
    ];

    const companyStats = [
        {
            icon: <Award className="w-12 h-12 text-yellow-500" />,
            value: "25+",
            label: "Years of Experience"
        },
        {
            icon: <Users className="w-12 h-12 text-blue-500" />,
            value: "100+",
            label: "Satisfied Clients"
        },
        {
            icon: <CheckCircle className="w-12 h-12 text-green-500" />,
            value: "500+",
            label: "Completed Projects"
        }
    ];

    const technicalCapabilities = [
        {
            icon: <Globe className="w-10 h-10 text-blue-600" />,
            title: "Global Standards",
            description: "Adhering to international electrical and safety standards"
        },
        {
            icon: <ZapIcon className="w-10 h-10 text-green-600" />,
            title: "Custom Solutions",
            description: "Tailored engineering approaches for unique client needs"
        },
        {
            icon: <BarChart2 className="w-10 h-10 text-purple-600" />,
            title: "Performance Optimization",
            description: "Advanced analytics and system performance enhancement"
        }
    ];

  
        const teamProfiles = [
            {
                name: "Mr. Edward Asante Boamah",
                role: "General Manager",
                image: "/assets/edwardboamah.png?v=20251015",
                expertise: [
                    "Project Engineering & Management",
                    "Geological Engineering (Engineering Management)",
                    "Telecommunications & Transmission Engineering",
                    "Operations Management and Service Direction",
                    "Green Energy Power Setup Applications"
                ],
                background: "Results-driven leader with proven experience across transmission engineering, operations, and large-scale technical programs. Holds BSc and MSc in Geological Engineering (majors in Project and Engineering Management) from Banaska Technical University Ostrava. Certified on Harris-Farscan and Microstar Microwave Radio; ZTE ZXMP-S320 & ZXC10-BSSB. Committed to a vision where electricity underpins quality of life, championing employee development and exceptional customer service.",
                skills: ["Leadership", "Project Management", "Problem Solving", "Communication", "Teamwork"]
            },
            {
                name: "Ezekile Komla Gbogbtsi",
                role: "Administration Manager",
                image: "/assets/ezekile.png?v=20251015",
                expertise: [
                    "Administrative Operations",
                    "Policy and Compliance Support",
                    "Vendor and Records Management",
                    "Cross-Functional Coordination",
                    "Service Quality Enablement"
                ],
                background: "Administration Manager supporting EEL's professionalism and excellence. Contributes to cohesive operations across technically skilled teams, enabling value-added solutions and outstanding service that exceeds client expectations, aligned with integrity, reliability, commitment, and collaboration.",
                skills: ["Administration", "Organizational Leadership", "Coordination", "Communication", "Reliability"]
            },
            {
                name: "Richard Boamah Asinor",
                role: "Technical Sales & Installations Lead",
                image: "/assets/asinor.jpg", 
                expertise: [
                    "25+ Years Electrical Engineering Experience",
                    "Advanced Technical Solutions Design",
                    "Strategic Project Implementation"
                ],
                background: "Spearheads technical sales, installations, and drives innovation in electrical infrastructure solutions.",
                skills: ["Project Management", "Technical Consulting", "Energy Systems"]
            },
            {
                name: "Technical Leadership Team",
                role: "Engineering & Innovation Specialists",
                image: "/assets/team.jpg", 
                expertise: [
                    "Annual Technical Training Programs",
                    "Multidisciplinary Technical Competence",
                    "Advanced Technological Integration"
                ],
                background: "Highly skilled team committed to continuous learning and delivering cutting-edge electrical solutions.",
                skills: ["System Design", "Cybersecurity", "Renewable Energy"]
            }
        ];
    
        // const [activeProfile, setActiveProfile] = useState(0);

        const handlePrevProfile = () => {
            setActiveProfile((prev) => (prev - 1 + teamProfiles.length) % teamProfiles.length);
        };
        
        const handleNextProfile = () => {
            setActiveProfile((prev) => (prev + 1) % teamProfiles.length);
        };
        
        const current = teamProfiles[activeProfile];

    const projectHighlights = [
        {
            title: "Gateway Services X-Ray Installation",
            description: "Cutting-edge scanning technology deployment",
            image: "/assets/nicktc.jpg"
        },
        {
            title: "UPSA Hostel Electrical System",
            description: "Comprehensive electrical infrastructure upgrade",
            image: "/assets/upsa.jpg"
        },
        {
            title: "Solar Power Implementation",
            description: "Sustainable energy solution for commercial facility",
            image: "/assets/solar.png"
        }
    ];

    return (
        <div className="bg-gray-50 min-h-screen">

            <header className="relative h-screen flex items-center bg-gradient-to-br from-blue-900 to-blue-600">
                {/* Background Image Overlay */}
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{
                        backgroundImage: 'url(/assets/9.jpg)'
                    }}
                />

                <div className="container mx-auto px-6 text-white relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="max-w-2xl"
                    >
                        <h1 className="text-5xl font-bold mb-6">
                            Transforming Electrical Solutions
                        </h1>
                        <p className="text-xl mb-8">
                            25+ Years of Engineering Excellence in Power Systems, Renewable Energy, and Advanced Technical Infrastructure
                        </p>
                        
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="bg-white text-blue-900 px-8 py-3 rounded-full flex items-center gap-2"
                            onClick={() => navigate('/services')}
                        >   
                            Explore Services <ArrowRight />
                        </motion.button>
                    </motion.div>
                </div>
            </header>

            {/* Services Section */}
            <section className="container mx-auto py-20 px-6">
                <h2 className="text-4xl font-bold text-center mb-16">Our Specialized Services</h2>
                <div className="grid md:grid-cols-3 gap-8">
                    {services.map((service, index) => (
                        <motion.div
                            key={index}
                            whileHover={{ scale: 1.05 }}
                            onClick={() => setActiveService(service)}
                            className="bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition"
                        >
                            <div className="mb-4">{service.icon}</div>
                            <h3 className="text-xl font-semibold mb-4">{service.title}</h3>
                            <p className="text-gray-600 mb-4">{service.description}</p>
                            <div className="flex items-center text-blue-600">
                                Learn More <ArrowRight className="ml-2" />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Company Stats */}
            <section className="bg-blue-900 text-white py-20">
                <div className="container mx-auto px-6">
                    <div className="grid md:grid-cols-3 gap-8 text-center">
                        {companyStats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.2 }}
                                className="flex flex-col items-center"
                            >
                                <div className="mb-4">{stat.icon}</div>
                                <div className="text-5xl font-bold mb-2">{stat.value}</div>
                                <div className="text-blue-200">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Project Highlights */}
            <section className="bg-white py-20">
                <div className="container mx-auto px-6">
                    <h2 className="text-4xl font-bold text-center mb-16">Project Highlights</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {projectHighlights.map((project, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.05 }}
                                className="bg-gray-100 rounded-xl overflow-hidden shadow-lg"
                            >
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-48 object-cover"
                                />
                                <div className="p-6">
                                    <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                                    <p className="text-gray-600">{project.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-8 md:py-20">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header - Responsive text sizes */}
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 md:mb-4 flex justify-center items-center gap-2 md:gap-4">
            <Award className="w-6 h-6 md:w-8 md:h-8 text-yellow-400" />
            Our Technical Leadership
          </h2>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto px-4">
            Driving innovation through expertise, continuous learning, and technical excellence
          </p>
        </div>

        {/* Main content - Stack on mobile, side by side on desktop */}
        <div className="flex flex-col md:grid md:grid-cols-2 gap-6 md:gap-8 items-center">
          {/* Profile section */}
          <div className="w-full flex items-center justify-center space-x-2 md:space-x-4">
            <button 
              onClick={handlePrevProfile}
              className="bg-blue-800 p-2 md:p-3 rounded-full hover:bg-blue-700 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            <div className="text-center flex-1">
              <div className="rounded-full w-48 h-48 md:w-64 md:h-64 mx-auto mb-4 md:mb-6 overflow-hidden border-4 border-white shadow-xl">
                <img 
                  src={current.image} 
                  alt={current.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">{current.name}</h3>
              <p className="text-lg md:text-xl text-blue-200">{current.role}</p>
            </div>

            <button 
              onClick={handleNextProfile}
              className="bg-blue-800 p-2 md:p-3 rounded-full hover:bg-blue-700 transition-colors"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* Details section */}
          <div className="bg-white/10 p-6 md:p-8 rounded-xl w-full">
            <h4 className="text-xl md:text-2xl font-semibold mb-3 md:mb-4 flex items-center">
              <Code className="mr-2 md:mr-3 text-green-400 w-5 h-5 md:w-6 md:h-6" />
              Professional Profile
            </h4>
            <p className="text-blue-100 mb-4 md:mb-6 text-sm md:text-base">{current.background}</p>
            
            <div className="mb-4 md:mb-6">
              <h5 className="font-bold mb-2 md:mb-3 text-blue-200 text-sm md:text-base">Core Expertise</h5>
              <ul className="space-y-1 md:space-y-2">
                {current.expertise.map((exp, idx) => (
                  <li key={idx} className="flex items-center text-sm md:text-base">
                    <ArrowRight className="mr-2 text-yellow-400 w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                    <span>{exp}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="font-bold mb-2 md:mb-3 text-blue-200 text-sm md:text-base">Key Skills</h5>
              <div className="flex flex-wrap gap-2">
                {current.skills.map((skill, idx) => (
                  <span 
                    key={idx} 
                    className="bg-blue-800 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

            {/* Technical Capabilities Section */}
            <section className="bg-white py-20">
                <div className="container mx-auto px-6">
                    <h2 className="text-4xl font-bold text-center mb-16">Our Technical Edge</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {technicalCapabilities.map((capability, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.05 }}
                                className="bg-gray-100 p-8 rounded-xl text-center"
                            >
                                <div className="flex justify-center mb-6">{capability.icon}</div>
                                <h3 className="text-xl font-semibold mb-4">{capability.title}</h3>
                                <p className="text-gray-600">{capability.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>




            {/* Contact CTA */}
            <section className="bg-blue-900 text-white py-20">
                <div className="container mx-auto text-center px-6">
                    <h2 className="text-4xl font-bold mb-8">Ready to Transform Your Electrical Infrastructure?</h2>
                    <p className="text-xl mb-12">
                        Let's discuss how Escort Energi can deliver innovative solutions for your business.
                    </p>
                    <div className="flex justify-center gap-6">
                        <motion.a
                            href="tel:+233302973531"
                            whileHover={{ scale: 1.05 }}
                            className="bg-white text-blue-900 px-8 py-3 rounded-full flex items-center gap-2"
                        >
                            <Zap /> Call Us
                        </motion.a>
                        <motion.a
                            href="mailto:escortenergitech@gmail.com"
                            whileHover={{ scale: 1.05 }}
                            className="border-2 border-white text-white px-8 py-3 rounded-full flex items-center gap-2"
                        >
                            <Clock /> Schedule Consultation
                        </motion.a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;